import React, { useState, useEffect, useRef } from 'react';
import { User as UserIcon, Mail, Phone, MapPin, Save, AlertCircle, CheckCircle, Camera, Trash2, Globe, Languages, Hash, ShieldCheck, ShieldOff } from 'lucide-react';
import { useKeycloak } from '@react-keycloak/web';
import { getApiUrl } from '../../config/api';

interface ProfileData {
  id: string;
  username: string; // READ-ONLY
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  phone: string;
  address: string;
  country: string;
  languages: string;
  taxId: string;
  profilePicture: string;
  emailVerified: boolean;
  enabled: boolean;
  createdTimestamp?: number;
  twoFactorEnabled: boolean;
  twoFactorPending: boolean;
}

interface ProfileEditProps {
  userId?: string;
  currentUser?: any;
  onCancel?: () => void;
}
const ProfileEdit: React.FC<ProfileEditProps> = ({ onCancel }) => {
  const { keycloak } = useKeycloak();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [updating2fa, setUpdating2fa] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    country: '',
    languages: '',
    taxId: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userId = keycloak.subject;
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await fetch(getApiUrl(`api/users/${userId}/profile`));
      if (!response.ok) {
        throw new Error('Failed to load profile');
      }

      const data = await response.json();
      setProfile(data);
      setFormData({
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        country: data.country || '',
        languages: data.languages || '',
        taxId: data.taxId || ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
      console.error('Error loading profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const userId = keycloak.subject;
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await fetch(getApiUrl(`api/users/${userId}/profile`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }

      await response.json();
      setSuccess('✅ Profile updated successfully!');
      
      // Refresh profile data
      await fetchProfile();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(`❌ Invalid file type: ${file.type}. Please upload an image file (JPEG, PNG, GIF, or WebP)`);
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setError(`❌ Image size is ${fileSizeMB}MB. Maximum allowed size is 5MB. Please compress or resize your image.`);
      return;
    }

    try {
      setUploadingPicture(true);
      setError(null);

      const userId = keycloak.subject;
      if (!userId) throw new Error('User ID not found');

      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await fetch(getApiUrl(`api/users/${userId}/profile-picture`), {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Upload failed with status ${response.status}`);
        } else {
          // Server returned HTML or other non-JSON content
          await response.text();
          if (response.status === 413) {
            throw new Error('❌ File too large. The image exceeds server limits. Please use a smaller file.');
          } else if (response.status === 415) {
            throw new Error('❌ Unsupported file format. Please upload a valid image file (JPEG, PNG, GIF, or WebP).');
          } else {
            throw new Error(`❌ Server error (${response.status}). Please try again or contact support.`);
          }
        }
      }

      await response.json();
      setSuccess('✅ Profile picture uploaded successfully!');
      
      // Update profile with new picture
      await fetchProfile();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload profile picture';
      setError(errorMessage.startsWith('❌') ? errorMessage : `❌ ${errorMessage}`);
      console.error('Error uploading picture:', err);
    } finally {
      setUploadingPicture(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePicture = async () => {
    if (!confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }

    try {
      setUploadingPicture(true);
      setError(null);

      const userId = keycloak.subject;
      if (!userId) throw new Error('User ID not found');

      const response = await fetch(getApiUrl(`api/users/${userId}/profile-picture`), {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete profile picture');
      }

      setSuccess('✅ Profile picture removed successfully!');
      
      // Refresh profile
      await fetchProfile();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete profile picture');
      console.error('Error deleting picture:', err);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleEnable2FA = async () => {
    try {
      setUpdating2fa(true);
      setError(null);
      const userId = keycloak.subject;
      if (!userId) throw new Error('User ID not found');

      const response = await fetch(getApiUrl(`api/users/${userId}/2fa/enable`), { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to enable two-factor authentication');
      }

      setSuccess('✅ Two-factor authentication will be set up next time you log in. Log out and back in to scan the QR code.');
      await fetchProfile();
      setTimeout(() => setSuccess(null), 6000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable two-factor authentication');
    } finally {
      setUpdating2fa(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!confirm('Turn off two-factor authentication for your account?')) return;
    try {
      setUpdating2fa(true);
      setError(null);
      const userId = keycloak.subject;
      if (!userId) throw new Error('User ID not found');

      const response = await fetch(getApiUrl(`api/users/${userId}/2fa/disable`), { method: 'POST' });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to disable two-factor authentication');
      }

      setSuccess('✅ Two-factor authentication disabled.');
      await fetchProfile();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disable two-factor authentication');
    } finally {
      setUpdating2fa(false);
    }
  };

  // Profile completeness — counts the fields a user is expected to fill in,
  // shown as a "your profile is N% complete" nudge like the reference design.
  const completionFields = [
    formData.firstName,
    formData.lastName,
    formData.email,
    formData.phone,
    formData.address,
    formData.country,
    formData.languages,
    formData.taxId,
    profile?.profilePicture
  ];
  const completionPercent = Math.round(
    (completionFields.filter(f => !!f && f.trim?.() !== '').length / completionFields.length) * 100
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-8 mb-6">
        <div className="flex items-center space-x-4">
          <div className="relative group">
            {profile?.profilePicture ? (
              <img
                src={getApiUrl(profile.profilePicture.replace(/^\//, ''))}
                alt="Profile"
                className="w-20 h-20 rounded-full object-cover border-4 border-white shadow-lg"
              />
            ) : (
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center text-blue-600 text-2xl font-bold shadow-lg">
                {profile?.firstName?.[0]}{profile?.lastName?.[0]}
              </div>
            )}
            
            {/* Upload/Delete buttons overlay */}
            <div className="absolute inset-0 rounded-full bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center space-x-1">
              <button
                onClick={handlePictureClick}
                disabled={uploadingPicture}
                className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                title="Upload picture"
              >
                <Camera className="w-4 h-4 text-gray-700" />
              </button>
              {profile?.profilePicture && (
                <button
                  onClick={handleDeletePicture}
                  disabled={uploadingPicture}
                  className="p-2 bg-white rounded-full hover:bg-red-50 transition-colors"
                  title="Remove picture"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              )}
            </div>
            
            {uploadingPicture && (
              <div className="absolute inset-0 rounded-full bg-black bg-opacity-70 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            )}
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handlePictureChange}
              className="hidden"
            />
          </div>
          <div className="text-white">
            <h1 className="text-3xl font-bold">{profile?.name || 'User Profile'}</h1>
            <p className="text-blue-100 mt-1">@{profile?.username}</p>
            {profile?.emailVerified && (
              <span className="inline-flex items-center mt-2 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircle className="w-3 h-3 mr-1" />
                Email Verified
              </span>
            )}
          </div>
        </div>
        <p className="text-sm text-blue-100 mt-4">
          <Camera className="inline w-4 h-4 mr-1" />
          Hover over your avatar to upload or remove your profile picture
        </p>

        {/* Profile completeness */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-blue-100 mb-1.5">
            <span>Profile Completeness</span>
            <span className="font-semibold text-white">{completionPercent}%</span>
          </div>
          <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-500"
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          {completionPercent < 100 && (
            <p className="text-xs text-blue-100 mt-1.5">
              Fill in the remaining fields below to complete your profile.
            </p>
          )}
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
            <p className="text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Profile Form */}
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit Profile</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Username (Read-Only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username (Read-Only)
            </label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={profile?.username || ''}
                disabled
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">Username cannot be changed</p>
          </div>

          {/* First Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              First Name *
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              className="px-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your first name"
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Last Name *
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              className="px-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your last name"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address *
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="your.email@example.com"
              />
            </div>
          </div>

          {/* Phone (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number (Optional)
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+1 (555) 123-4567"
              />
            </div>
          </div>

          {/* Address (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address (Optional)
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows={3}
                className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Enter your address"
              />
            </div>
          </div>

          {/* Country / Languages / Tax ID */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country (Optional)</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. India"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Languages (Optional)</label>
              <div className="relative">
                <Languages className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="languages"
                  value={formData.languages}
                  onChange={handleChange}
                  className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. English, Tamil"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tax ID (Optional)</label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="taxId"
                  value={formData.taxId}
                  onChange={handleChange}
                  className="pl-10 pr-4 py-3 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. Tax-1234"
                />
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Account Information</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <p>Account Status: <span className="font-medium text-green-600">{profile?.enabled ? 'Active' : 'Disabled'}</span></p>
              {profile?.createdTimestamp && (
                <p>Member Since: <span className="font-medium">{new Date(profile.createdTimestamp).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span></p>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => onCancel?.()}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={saving}
              title="Go to Dashboard"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Security — Two-Factor Authentication */}
      <div className="bg-white rounded-lg shadow-lg p-8 mt-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-1">Security</h2>
        <p className="text-sm text-gray-500 mb-6">Protect your account with an authenticator app.</p>

        <div className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            {profile?.twoFactorEnabled ? (
              <ShieldCheck className="w-8 h-8 text-green-600" />
            ) : (
              <ShieldOff className="w-8 h-8 text-gray-400" />
            )}
            <div>
              <p className="font-semibold text-gray-900">Two-Factor Authentication</p>
              <p className="text-sm text-gray-500">
                {profile?.twoFactorEnabled
                  ? 'Enabled — an authenticator code is required at login.'
                  : profile?.twoFactorPending
                  ? 'Set up pending — you\'ll be asked to scan a QR code next time you log in.'
                  : 'Not enabled. Add an extra layer of security to your account.'}
              </p>
            </div>
          </div>
          {profile?.twoFactorEnabled || profile?.twoFactorPending ? (
            <button
              onClick={handleDisable2FA}
              disabled={updating2fa}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {updating2fa ? 'Please wait...' : 'Disable 2FA'}
            </button>
          ) : (
            <button
              onClick={handleEnable2FA}
              disabled={updating2fa}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              {updating2fa ? 'Please wait...' : 'Enable 2FA'}
            </button>
          )}
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Your username cannot be changed for security reasons. All other fields can be updated as needed. Fields marked with * are required.
        </p>
      </div>
    </div>
  );
};

export default ProfileEdit;
